import { css, cx } from "@emotion/css";
import { pxToRem } from "@mos-cat/ds-theme";
import type { SystemStyleObject } from "@styled-system/css";
import { createComponent, createHook } from "reakit-system";
import { variant } from "styled-system";
import type { BoxProps } from "../Box/Box";
import { useBox } from "../Box/Box";
import { useText } from "../Text/Text";
import { WHCM } from "../constants";
import { useFocusable, useTheme } from "../hooks";
import { customStyles } from "./inner/customStyles";
import type { ComposedOptions, FieldHTMLProps, FieldOptions, FieldProps } from "./types";

export type { ComposedOptions, FieldHTMLProps, FieldOptions, FieldProps };

const error = variant({
  prop: "validation",
  variants: {
    valid: {},
    invalid: {
      borderColor: "errorColor",
      ":hover": {
        borderColor: "errorColor",
      },

      [WHCM]: {
        "&, &:hover": {
          borderColor: "Highlight",
        },
      },
    },
  },
});

// Отдельным хуком, чтобы он переписал значения border
let useValidation = createHook<{ hasError: boolean }, FieldHTMLProps>({
  keys: ["hasError"],
  compose: [useTheme],
  useOptions(options) {
    return { ...options, validation: options.hasError ? "invalid" : "valid" };
  },
  useProps(options, props) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- не можем влиять на типы из styled-system
    return { ...props, className: cx(props.className, css(error(options))) };
  },
});

export const useField = createHook<FieldOptions, FieldHTMLProps>({
  name: "Field",
  keys: ["hasError", "disabled", "horSize"],
  compose: [useBox, useText, useFocusable, useValidation],
  useOptions(options: FieldOptions, props: FieldHTMLProps): ComposedOptions {
    let maxWidth: BoxProps["width"] = 1;

    if (options.horSize === "size-1") maxWidth = 1;
    if (options.horSize === "size-2") maxWidth = pxToRem(432);
    if (options.horSize === "size-3") maxWidth = pxToRem(320);
    if (options.horSize === "size-4") maxWidth = pxToRem(208);
    if (options.horSize === "size-5") maxWidth = pxToRem(152);

    let WebkitTextFillColor = props.disabled ? "fieldDisabledTextColor" : undefined;
    return {
      borderRadius: "fieldBorderRadius",
      paddingX: "fieldPaddingX",
      paddingY: "fieldPaddingY",
      minHeight: pxToRem(48),
      width: "100%",
      maxWidth,
      ...options,
      border: 1,
      borderColor: "fieldBorderColor",
      backgroundColor: "fieldBgColor",
      fontSize: "textBase",
      color: "fieldTextColor",
      WebkitTextFillColor,

      styles: {
        ...options.styles,
        ...customStyles,
      } as SystemStyleObject,
    };
  },
  useProps({ disabled }, props) {
    let hasValue = props.value ?? props.defaultValue ?? "";

    return {
      ...props,
      "aria-disabled": disabled,
      onChange: disabled ? undefined : props.onChange,
      onKeyDown: disabled
        ? (event) => {
            if (event.key !== "Tab") {
              event.preventDefault();
            }
          }
        : props.onKeyDown,
      value: disabled ? hasValue : props.value,
      className: cx(css({ label: "Field" }), props.className),
    };
  },
});

export const Field = createComponent<"input", FieldOptions>({
  as: "input",
  useHook: useField,
  memo: true,
});
